import { useState, useEffect, useMemo } from 'react'
import { Row, Col, Card, Button, Switch, Select, Input, Typography, Divider } from 'antd'
import { useNavigate } from 'react-router-dom'
import { getArenaGames, updateArenaGameStatus } from 'src/api/arena'
import PrivateImage from 'src/components/PrivateImage'
import './ArenaGames.css'

const { Title, Text } = Typography
const { Search } = Input

const ACTIVE: ArenaGameStatus = 'ACTIVE'
const TEMPORARY_UNAVAILABLE: ArenaGameStatus = 'TEMPORARY_UNAVAILABLE'

const STATUS_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Active', value: ACTIVE },
  { label: 'Temporary Unavailable', value: TEMPORARY_UNAVAILABLE },
  { label: 'Coming Soon', value: 'COMING_SOON' },
]

const getStatus = (game: ArenaGame): ArenaGameStatus => game.status ?? ACTIVE

const ArenaGames = (): JSX.Element => {
  const navigate = useNavigate()
  const [games, setGames] = useState<ArenaGame[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [searchInput, setSearchInput] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [status, setStatus] = useState<string>('')

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setLoading(true)
      try {
        const data = await getArenaGames()
        setGames(data)
      } catch (error) {
        console.error('Error fetching arena games:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const filteredGames = useMemo(
    () =>
      games.filter((game) => {
        const matchesName = (game.title ?? '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
        const matchesStatus = !status || getStatus(game) === status
        return matchesName && matchesStatus
      }),
    [games, searchTerm, status]
  )

  const handleToggleStatus = async (
    game: ArenaGame,
    checked: boolean
  ): Promise<void> => {
    const newStatus: ArenaGameStatus = checked ? ACTIVE : TEMPORARY_UNAVAILABLE
    // optimistic update
    setGames((prev) =>
      prev.map((g) =>
        g.gameId === game.gameId ? { ...g, status: newStatus } : g
      )
    )
    const result = await updateArenaGameStatus(game.gameId, newStatus)
    if (result === 'failed') {
      // revert on failure
      setGames((prev) =>
        prev.map((g) =>
          g.gameId === game.gameId ? { ...g, status: getStatus(game) } : g
        )
      )
    }
  }

  const goToEdit = (game: ArenaGame): void => {
    const params = new URLSearchParams()
    params.set('title', game.title ?? '')
    params.set('status', getStatus(game))
    if (game.smallThumbnailS3Path)
      params.set('thumbnail', game.smallThumbnailS3Path)
    if (game.fullS3Path) params.set('inner', game.fullS3Path)
    if (game.challengeS3Path) params.set('dailyChallenge', game.challengeS3Path)
    if (game.thumbnailS3Path) params.set('bannerHolder', game.thumbnailS3Path)
    if (game.unavailableS3Path) params.set('maintenance', game.unavailableS3Path)
    if (game.comingSoonS3Path) params.set('comingSoon', game.comingSoonS3Path)
    if (game.splashS3Path) params.set('splash', game.splashS3Path)
    if (game.videoS3Path) params.set('video', game.videoS3Path)
    navigate(`/arena/games/edit/${game.gameId}?${params.toString()}`)
  }

  return (
    <div>
      <Title level={3}>
        <Text>Arena Games</Text>
      </Title>
      <Divider style={{ margin: '10px 0' }} />

      <div className="arena-games-filters">
        <Search
          className="search-input"
          style={{ width: 500 }}
          placeholder="Search Game"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={setSearchTerm}
          allowClear
        />
        <Select
          className="search-input"
          style={{ width: 500 }}
          value={status}
          options={STATUS_OPTIONS}
          onChange={setStatus}
        />
        <Button
          type="primary"
          className="btn-primary"
          style={{ width: 100 }}
          onClick={() => setSearchTerm(searchInput)}
        >
          Search
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        {filteredGames.map((game) => {
          const active = getStatus(game) === ACTIVE
          return (
            <Col key={game.gameId} xs={24} sm={12} md={8} lg={6} xl={4}>
              <Card
                className="arena-game-card"
                loading={loading}
                cover={
                  <PrivateImage
                    src={game.fullS3Path ?? game.thumbnailS3Path}
                    width="100%"
                    height={160}
                  />
                }
              >
                <div className="arena-game-name">{game.title}</div>
                <div className="arena-game-actions">
                  <Switch
                    checked={active}
                    onChange={(checked) => handleToggleStatus(game, checked)}
                  />
                  <Button type="primary" className="btn-primary" style={{ width: 100 }} onClick={() => goToEdit(game)}>
                    Edit
                  </Button>
                </div>
              </Card>
            </Col>
          )
        })}
      </Row>
    </div>
  )
}

ArenaGames.displayName = 'ArenaGames'

export default ArenaGames
